import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CampaignProgress {
  total: number;
  sent: number;
  failed: number;
  status: string | null;
  isFinished: boolean;
}

/** Délai de grâce avant de considérer un statut "draft" comme un envoi terminé/annulé. */
const DRAFT_GRACE_MS = 90_000;

async function countRecipients(campaignId: string, status?: string) {
  let q = supabase
    .from('email_campaign_recipients')
    .select('id', { count: 'exact', head: true })
    .eq('campaign_id', campaignId);
  if (status) q = q.eq('status', status);
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

export function useCampaignProgress(campaignId: string | null) {
  // Suivi local du cycle de vie : on ne conclut pas avant d'avoir vu l'envoi démarrer
  const lifecycleRef = useRef<{ campaignId: string | null; startedAt: number; sawSending: boolean }>({
    campaignId: null,
    startedAt: Date.now(),
    sawSending: false,
  });

  if (lifecycleRef.current.campaignId !== campaignId) {
    lifecycleRef.current = { campaignId, startedAt: Date.now(), sawSending: false };
  }

  return useQuery<CampaignProgress>({
    queryKey: ['campaign-progress', campaignId],
    queryFn: async () => {
      const [campaignRes, total, sent, failed] = await Promise.all([
        supabase.from('email_campaigns').select('status').eq('id', campaignId!).maybeSingle(),
        countRecipients(campaignId!),
        countRecipients(campaignId!, 'sent'),
        countRecipients(campaignId!, 'failed'),
      ]);

      const status = (campaignRes.data?.status as string | undefined) ?? null;
      if (status === 'sending') lifecycleRef.current.sawSending = true;

      // 'draft' avant le démarrage réel = course avec l'edge function : on continue de sonder
      const draftIsTerminal =
        lifecycleRef.current.sawSending ||
        Date.now() - lifecycleRef.current.startedAt > DRAFT_GRACE_MS;

      const isFinished =
        status === 'sent' ||
        status === 'failed' ||
        (status === 'draft' && draftIsTerminal);

      return { total, sent, failed, status, isFinished };
    },
    enabled: !!campaignId,
    refetchInterval: (query) => (query.state.data?.isFinished ? false : 2000),
    staleTime: 0,
  });
}

